"use client";

import { useMemo } from "react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { Sparkles } from "lucide-react";
import { ReferencePrimaryPrice } from "@/ui/components/pricing/reference-primary-price";
import { usePricingResolvePresentations } from "@/lib/pricing/use-pricing-resolve-presentations";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface BlendHintProps {
	blendProduct: unknown; // product from GraphQL
	individualTotal: number;
	currency: string;
	locale: string;
	channel: string;
}

type ProductRecord = Record<string, unknown>;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getBlendPrice(product: unknown): number | null {
	const p = product as ProductRecord | null;
	if (!p) return null;

	const variants = p.variants as Array<Record<string, unknown>> | undefined;
	if (!variants || variants.length === 0) return null;

	// Use first variant's gross price as the blend price
	const firstVariant = variants[0];
	if (!firstVariant) return null;

	const pricing = firstVariant.pricing as Record<string, unknown> | undefined;
	if (!pricing) return null;
	const price = pricing.price as Record<string, unknown> | undefined;
	if (!price) return null;
	const gross = price.gross as Record<string, unknown> | undefined;
	if (!gross) return null;
	const amount = gross.amount as number | undefined;
	return amount ?? null;
}

function getBlendSlug(product: unknown): string | null {
	const p = product as ProductRecord | null;
	if (!p) return null;
	return (p.slug as string) ?? null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function BlendHint({ blendProduct, individualTotal, currency, locale, channel }: BlendHintProps) {
	const t = useTranslations("stackBuilder");

	const blendPrice = getBlendPrice(blendProduct);
	const blendSlug = getBlendSlug(blendProduct);

	const savingsAmount =
		blendPrice !== null && blendPrice < individualTotal ? individualTotal - blendPrice : 0;
	const savingsPercent =
		blendPrice !== null && individualTotal > 0 && blendPrice < individualTotal
			? Math.round((1 - blendPrice / individualTotal) * 100)
			: 0;

	const saveItems = useMemo(
		() => (savingsAmount > 0 && savingsPercent > 0 ? [{ id: "save", amount: savingsAmount, currency }] : []),
		[savingsAmount, currency, savingsPercent],
	);
	const saveMap = usePricingResolvePresentations(locale, channel, saveItems);
	const savePres = saveMap.get("save");

	const formattedSavings = new Intl.NumberFormat(locale, {
		style: "currency",
		currency,
	})
		.format(savingsAmount > 0 ? savingsAmount : 0)
		.replace(/\u202F/g, "\u00A0");

	// Need a valid blend price less than the individual total to show savings
	if (blendPrice === null || blendPrice >= individualTotal || !blendSlug || savingsPercent <= 0) return null;

	const blendUrl = `/${locale}/${channel}/products/${blendSlug}`;

	return (
		<div className="border-accent/30 bg-accent/5 flex items-start gap-2.5 rounded-lg border px-3 py-2.5">
			<Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden="true" />
			<div className="min-w-0 flex-1">
				<p className="text-xs font-semibold text-foreground">{t("blendHint", { percent: savingsPercent })}</p>
				<p className="mt-0.5 text-[11px] text-muted-foreground">
					Save{" "}
					{savePres ? (
						<ReferencePrimaryPrice
							presentation={savePres}
							locale={locale}
							channel={channel}
							size="sm"
							className="inline text-[11px] text-muted-foreground"
							showReferenceTooltip={false}
						/>
					) : (
						formattedSavings
					)}{" "}
					vs buying individually.
				</p>
				<Link
					href={blendUrl}
					className="mt-1 inline-flex items-center text-[11px] font-semibold text-accent underline-offset-2 hover:underline"
				>
					{t("viewBlend")} &rarr;
				</Link>
			</div>
		</div>
	);
}
